import { useAccountContext } from "~/v2/providers/AccountProvider";
import { isCryptoSymbol } from "~/v3-poc/api/watchlistMarketData";
import { useAtdThemeContext } from "~/v3-poc/features/user-preferences";
import { useOrderTicketDrawer } from "../../orderTicketDrawer/OrderTicketDrawer.context";
import { useGroupSymbolMutation } from "../../shared/groups/useGroupSymbolMutation";
import {
  useWorkspace,
  useWorkspaceStore,
} from "../../shared/workspace/Workspace.context";
import {
  getEffectiveWidgetDateRange,
  getEffectiveWidgetSymbol,
} from "../../shared/workspace/workspace.utils";
import { useChartOrderOverlays } from "./ChartWidget.controller";
import { ChartWidgetPresentation } from "./ChartWidget.pres";
import { TradingViewChart } from "./tradingView/TradingViewChart.connector";
import {
  resolveIntervalForRange,
  workspaceDateRangeSpanDays,
  workspaceDateRangeToUnixSeconds,
} from "./tradingView/tradingView.utils";

export interface ChartWidgetProps {
  id: string;
}

const DEFAULT_CHART_SYMBOL = "SPY";

/**
 * Read-only Advanced Charts market-data consumer. It renders the group's
 * effective symbol or SPY as a display fallback, and never publishes state back.
 */
export const ChartWidget = ({ id }: ChartWidgetProps) => {
  const { theme } = useAtdThemeContext();
  const { currentAccount, product } = useAccountContext();
  const { openOrderTicket } = useOrderTicketDrawer();
  const { setGroupSymbol } = useGroupSymbolMutation();
  const store = useWorkspaceStore();
  const groupId = useWorkspace((state) => state.widgets[id]?.groupId ?? null);
  const savedState = useWorkspace((state) => {
    const settings = state.widgetSettings[id];
    return settings?.kind === "chart" ? settings.state : undefined;
  });
  const comparisonSymbols = useWorkspace((state) => {
    const settings = state.widgetSettings[id];
    return settings?.kind === "chart" ? settings.comparisonSymbols : undefined;
  });
  // Primitive selectors: safe under Zustand v5 referential-equality bailout.
  const symbol =
    useWorkspace((s) => getEffectiveWidgetSymbol(s, id)) ??
    DEFAULT_CHART_SYMBOL;
  const dateFrom = useWorkspace(
    (s) => getEffectiveWidgetDateRange(s, id)?.from,
  );
  const dateTo = useWorkspace((s) => getEffectiveWidgetDateRange(s, id)?.to);

  const dateRange =
    dateFrom && dateTo
      ? workspaceDateRangeToUnixSeconds({ from: dateFrom, to: dateTo })
      : undefined;
  // Derived resolution for the span. The TradingView controller applies
  // interval changes via setResolution (and dateRange via setVisibleRange)
  // without remounting when the span crosses a threshold.
  const interval =
    dateFrom && dateTo
      ? resolveIntervalForRange(
          workspaceDateRangeSpanDays({ from: dateFrom, to: dateTo }),
        )
      : "15";
  const crypto = isCryptoSymbol(symbol);
  const {
    showOpenLimitOrders,
    showHistoricalOrderFills,
    openLimitOrders,
    historicalOrderFills,
    selectedOrderOverlay,
    onOpenLimitOrdersVisibleChange,
    onHistoricalOrderFillsVisibleChange,
    onOrderOverlaySelect,
    onOrderDetailsDismiss,
  } = useChartOrderOverlays({
    id,
    symbol,
    accountID: currentAccount?.id,
    product,
    store,
  });

  return (
    <ChartWidgetPresentation
      selectedOrderOverlay={selectedOrderOverlay}
      onOrderDetailsDismiss={onOrderDetailsDismiss}
      showOpenLimitOrders={showOpenLimitOrders}
      showHistoricalOrderFills={showHistoricalOrderFills}
      onOpenLimitOrdersVisibleChange={onOpenLimitOrdersVisibleChange}
      onHistoricalOrderFillsVisibleChange={onHistoricalOrderFillsVisibleChange}
      chart={
        <TradingViewChart
          container={`workspace-tv-${id}`}
          symbol={symbol}
          theme={theme}
          crypto={crypto}
          exchange={crypto ? "CRYPTO" : "NYSE"}
          interval={interval}
          height="100%"
          autosize
          baseUrl={window.env.DATA_API_URL}
          seriesStyle={1}
          dateRange={dateRange}
          savedState={savedState}
          onStateChange={(state) => store.getState().setChartState(id, state)}
          comparisonSymbols={comparisonSymbols}
          onSymbolChange={(selectedSymbol) => {
            if (groupId === null) {
              store.getState().setWidgetLocalSymbol(id, selectedSymbol);
              return;
            }
            setGroupSymbol(groupId, selectedSymbol);
          }}
          onComparisonSymbolsChange={(selectedSymbols) =>
            store.getState().setChartComparisonSymbols(id, selectedSymbols)
          }
          openLimitOrders={openLimitOrders}
          historicalOrderFills={historicalOrderFills}
          onOrderOverlaySelect={onOrderOverlaySelect}
          onContextMenu={(_unixTime, price) => {
            const limitPrice = Math.round(price * 100) / 100;
            const formattedPrice = limitPrice.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });

            return [
              {
                position: "top",
                text: `Buy limit @ $${formattedPrice}`,
                click: () =>
                  openOrderTicket({
                    symbol,
                    side: "buy",
                    orderType: "limit",
                    limitPrice,
                  }),
              },
              {
                position: "top",
                text: `Sell limit @ $${formattedPrice}`,
                click: () =>
                  openOrderTicket({
                    symbol,
                    side: "sell",
                    orderType: "limit",
                    limitPrice,
                  }),
              },
            ];
          }}
          enabledFeatures={[
            "remove_library_container_border",
            "disable_legend_inplace_symbol_change",
            "hide_left_toolbar_by_default",
          ]}
          disabledFeatures={["header_saveload", "main_series_scale_menu"]}
        />
      }
    />
  );
};
